import { useEffect, useRef, useState, type FormEvent } from 'react';

interface TopicsToolbarProps {
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  sort: 'heat' | 'new';
  onSortChange: (value: 'heat' | 'new') => void;
  total: number;
  hasQuery: boolean;
  onClear: () => void;
}

export default function TopicsToolbar({
  input,
  onInputChange,
  onSubmit,
  sort,
  onSortChange,
  total,
  hasQuery,
  onClear,
}: TopicsToolbarProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  const sortLabel = sort === 'heat' ? '按热度排序' : '按时间排序';

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between">
      <form onSubmit={handleSubmit} className="flex w-full items-center gap-2 md:max-w-md" role="search">
        <div className="flex h-11 flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 focus-within:border-primary-300">
          <i className="ri-search-line text-base text-foreground-400" aria-hidden="true" />
          <input
            type="search"
            value={input}
            onChange={(event) => onInputChange(event.target.value)}
            placeholder="输入关键词，如「换角」「恋综」"
            aria-label="搜索瓜料"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="flex h-11 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-search-line" aria-hidden="true" />
          搜索
        </button>
      </form>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-foreground-500">
          共 <strong className="text-foreground-900">{total}</strong> 条结果
        </span>
        {hasQuery && (
          <button
            type="button"
            onClick={onClear}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-2.5 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-close-line" aria-hidden="true" />
            清除筛选
          </button>
        )}
        <div ref={wrapRef} className="relative">
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300"
          >
            <i className="ri-sort-desc" aria-hidden="true" />
            {sortLabel}
            <i className={`ri-arrow-down-s-line transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
          {open && (
            <ul className="absolute right-0 z-20 mt-2 w-40 overflow-hidden rounded-md border border-background-200 bg-background-50 py-1 shadow-none">
              {[
                { key: 'heat', label: '按热度排序' },
                { key: 'new', label: '按时间排序' },
              ].map((option) => (
                <li key={option.key}>
                  <button
                    type="button"
                    onClick={() => {
                      onSortChange(option.key as 'heat' | 'new');
                      setOpen(false);
                    }}
                    className={`flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left text-xs transition-colors hover:bg-background-100 ${
                      sort === option.key ? 'font-semibold text-primary-700' : 'text-foreground-700'
                    }`}
                  >
                    {option.label}
                    {sort === option.key && <i className="ri-check-line" aria-hidden="true" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}